/**
 * ExtrudePieMenu — Alt+E radial menu grouping the four face-extrude variants.
 *
 * Options delegate to the registered operators in `modeling/operators.ts`
 * so keymap, palette, and this pie stay in sync: adding or removing an
 * extrude variant means touching the operator registry in one place.
 */

import { useCallback, useMemo } from "react";
import { getOperator } from "../modeling/operators.ts";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";
import { PieMenu, type PieMenuOption } from "./PieMenu.tsx";

const EXTRUDE_PIE_IDS = [
  "extrude",
  "extrude_along_normals",
  "extrude_manifold",
  "extrude_individual",
] as const;

export function ExtrudePieMenu() {
  const menu = useOperatorMenu("extrudePie");

  const { pieOptions, optionById } = useMemo(() => {
    const resolved = EXTRUDE_PIE_IDS.map((id) => getOperator(id)).filter(
      (op): op is NonNullable<ReturnType<typeof getOperator>> => op !== null,
    );
    return {
      pieOptions: resolved.map<PieMenuOption>((op) => ({
        id: op.id,
        label: op.label,
      })),
      optionById: new Map(resolved.map((op) => [op.id, op])),
    };
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      const op = optionById.get(id);
      menu.close();
      if (!op) return;
      op.execute();
    },
    [menu, optionById],
  );

  if (pieOptions.length === 0) return null;

  return (
    <PieMenu
      open={menu.open}
      onClose={menu.close}
      onSelect={handleSelect}
      anchorPoint={menu.anchorPoint}
      options={pieOptions}
      holdKey="e"
    />
  );
}
